import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    BimContractConversionIndexInput,
    BimContractLimitedCostInput,
    BimContractPlanInput,
    BimContractQuery,
    BimContractSeasonDistributionInput,
    BimContractSeasonInflationInput,
    BimCreateContractMutation,
    BimLimitedCostCalcMethodEnum,
    ConversionCoefficientsData,
} from 'gql/graphql';
import { BimTotal_AlcTypeEnum as BimTotalAlcTypeEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { IConstructionTypeListItem } from 'pages/Project/Common/utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';
import { replacerCost, replacerCostIfValue } from 'utils/replacerCost';
import type { TActionCommonInterface } from 'utils/types';

import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimCreateContractGql, bimUpdateContractGql } from '../../BimContractRequests';
import {
    ConversionCoefficientsKeys,
    getConversionIndicesCoefficientKey,
} from '../../hooks/useBimConversionIndicesColumns';
import type { LimitType } from '../components/types';

const contractPlanKeys = [
    BimTotalAlcTypeEnum.TotalChapter1_7,
    BimTotalAlcTypeEnum.TotalChapter8,
    BimTotalAlcTypeEnum.TotalChapter9,
    BimTotalAlcTypeEnum.TotalChapter12,
    BimTotalAlcTypeEnum.TotalChapter1_7Op,
    BimTotalAlcTypeEnum.TotalChapter1_7Oz,
    BimTotalAlcTypeEnum.TotalChapter1_7Mp,
    BimTotalAlcTypeEnum.TotalChapter1_7Mz,
];

const contractPlanInputKeys: Record<string, string> = {
    [BimTotalAlcTypeEnum.TotalChapter1_7]: 'chapter1_7',
    [BimTotalAlcTypeEnum.TotalChapter8]: 'chapter8',
    [BimTotalAlcTypeEnum.TotalChapter9]: 'chapter9',
    [BimTotalAlcTypeEnum.TotalChapter12]: 'chapter12',
    [BimTotalAlcTypeEnum.TotalChapter1_7Op]: 'equipmentContractor',
    [BimTotalAlcTypeEnum.TotalChapter1_7Oz]: 'equipmentCustomer',
    [BimTotalAlcTypeEnum.TotalChapter1_7Mp]: 'materialContractor',
    [BimTotalAlcTypeEnum.TotalChapter1_7Mz]: 'materialCustomer',
};

export const coefficientsPrepare = (formData: any, constructionTypeId: number): ConversionCoefficientsData => ({
    [ConversionCoefficientsKeys.kemm]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kemm, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.keqp]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.keqp, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.kmtr]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmtr, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.knr]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.knr, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.kozp]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kozp, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.kpir]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kpir, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.ksp]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.ksp, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.kzpm]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kzpm, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.kmz]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmz, constructionTypeId)],
    ),
    [ConversionCoefficientsKeys.koz]: replacerCostIfValue(
        formData[getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.koz, constructionTypeId)],
    ),
});

export const getConstructionTypeCoefficientsKeys = (constructionTypeId: number) => [
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kemm, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.keqp, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmtr, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.knr, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kozp, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kpir, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.ksp, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kzpm, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmz, constructionTypeId),
    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.koz, constructionTypeId),
];

export const getInflationInputByFlatField = (key: string, value: any): BimContractSeasonInflationInput => ({
    inflationRate: replacerCost(value),
    contractSeasonYear: Number(key.replace(/\D/g, '')),
});

const dataPrepare = (data: any) => {
    const seasonsInflation: BimContractSeasonInflationInput[] = [];
    const seasonsInflationKeysToOmit: string[] = [];

    const conversionIndices: BimContractConversionIndexInput[] = [];
    const conversionIndicesKeysToOmit: string[] = [];

    const transformData = Object.entries(data).reduce((acc: any, [key, value]: [any, any]) => {
        if (key === 'constructionTypes') {
            value.forEach((ct: IConstructionTypeListItem) => {
                conversionIndicesKeysToOmit.splice(
                    conversionIndicesKeysToOmit.length - 1,
                    0,
                    ...getConstructionTypeCoefficientsKeys(ct.id),
                );

                conversionIndices.push({
                    constructionTypeId: ct.id,
                    coefficients: coefficientsPrepare(data, ct.id),
                });
            });

            acc = {
                ...acc,
                constructionTypeIds: value.map((elConstructionTypes: any) => elConstructionTypes.id),
            };
        }

        if (key === 'generalInfo') {
            const convertGeneralInfo = R.omit(['contractorOKPO', 'customer', 'customerOKPO', 'okpd', 'okud'], {
                ...value,
                amountWithoutTax: replacerCost(value.amountWithoutTax),
            });

            acc = { ...acc, generalInfo: convertGeneralInfo };
        }

        if (key === 'limitedCosts') {
            const createLimitedCosts = data.constructionTypes.map(
                (constructionType: { id: number; label: string }) => ({
                    constructionTypeId: constructionType.id,
                    regionalFactor: replacerCostIfValue(data.limitedCosts[`regionalFactor_${constructionType.id}`]),
                    items:
                        data.limitedCosts[`typesLimitedCost_${constructionType.id}`]?.map((typeLimit: LimitType) => ({
                            rateValue: replacerCost(
                                data.limitedCosts[`rateValueLimitedCost_${constructionType.id}_${typeLimit.type}`],
                            ),
                            fotValue: replacerCost(
                                data.limitedCosts[`fotValueLimitedCost_${constructionType.id}_${typeLimit.type}`],
                            ),
                            type: typeLimit.type,
                        })) ?? [],
                }),
            );

            acc = { ...acc, limitedCosts: createLimitedCosts };
        }

        if (key === 'limitedCostCalcMethod') {
            acc = { ...acc, limitedCostCalcMethod: value.id };
        }

        if (key === 'otherDistributions') {
            const convertOtherDistributions = value.map((elOtherDistributions: any) => ({
                ...R.omit(['contractSeason', 'id'], elOtherDistributions),
                contractSeasonYear: elOtherDistributions.contractSeason.year,
            }));

            acc = { ...acc, otherDistributions: convertOtherDistributions };
        }

        if (key.startsWith('inflationRate')) {
            seasonsInflation.push(getInflationInputByFlatField(key, value));
            seasonsInflationKeysToOmit.push(key);
        }

        if (key === 'smrDistributions') {
            const convertSmrDistributions = value.map((elSmrDistributions: any) => ({
                ...R.omit(['contractSeason', 'id'], elSmrDistributions),
                contractSeasonYear: elSmrDistributions.contractSeason.year,
            }));

            acc = { ...acc, smrDistributions: convertSmrDistributions };
        }

        if (key === 'status') {
            acc = { ...acc, status: value.id };
        }

        if (key === 'seasonsRange') {
            const startSeasonYear = value[0].getFullYear();
            const finishSeasonYear = value[1].getFullYear();

            acc = {
                ...acc,
                startSeasonYear,
                finishSeasonYear,
            };
        }

        return acc;
    }, {});

    const smrDistributions: BimContractSeasonDistributionInput[] = [];
    const otherDistributions: BimContractSeasonDistributionInput[] = [];
    const plan: BimContractPlanInput[] = [];
    const accumulationFieldsToOmit: string[] = [];

    const dataKeys = Object.keys(data);

    transformData?.constructionTypeIds?.forEach((constructionTypeId: number) => {
        const planInput: BimContractPlanInput = {
            constructionTypeId,
        };

        contractPlanKeys.forEach((key) => {
            const actualKey = dataKeys.find((k) => k.includes(key) && k.endsWith(String(constructionTypeId)));

            if (actualKey) {
                const prop = contractPlanInputKeys[key];

                planInput[prop as keyof BimContractPlanInput] = replacerCost(data[actualKey]);

                accumulationFieldsToOmit.push(actualKey);
            }
        });

        plan.push(planInput);

        for (let index = 0; transformData.startSeasonYear + index <= transformData.finishSeasonYear; index++) {
            const contractSeasonYear = transformData.startSeasonYear + index;
            const seasonCostsKey = `${BimTotalAlcTypeEnum.SeasonCosts}_${contractSeasonYear}`;
            const seasonWithUnforeseenKey = `${BimTotalAlcTypeEnum.SeasonWithUnforeseenCosts}_${contractSeasonYear}`;
            const seasonCostsKeyActualKey = dataKeys.find(
                (k) => k.includes(seasonCostsKey) && k.endsWith(String(constructionTypeId)),
            );
            const seasonWithUnforeseenKeyActualKey = dataKeys.find(
                (k) => k.includes(seasonWithUnforeseenKey) && k.endsWith(String(constructionTypeId)),
            );

            if (seasonCostsKeyActualKey) {
                smrDistributions.push({
                    constructionTypeId,
                    contractSeasonYear,
                    seasonValue: replacerCost(data[seasonCostsKeyActualKey]),
                });

                accumulationFieldsToOmit.push(seasonCostsKeyActualKey);
            } else {
                smrDistributions.push({
                    constructionTypeId,
                    contractSeasonYear,
                });
            }

            if (seasonWithUnforeseenKeyActualKey) {
                otherDistributions.push({
                    constructionTypeId,
                    contractSeasonYear,
                    seasonValue: replacerCost(data[seasonWithUnforeseenKeyActualKey]),
                });

                accumulationFieldsToOmit.push(seasonWithUnforeseenKeyActualKey);
            } else {
                otherDistributions.push({
                    constructionTypeId,
                    contractSeasonYear,
                });
            }
        }
    });

    const limitedCostsFormFieldsToOmit = data.constructionTypes?.map(
        (constructionType: { id: any }) => `typesLimitedCost_${constructionType.id}`,
    );

    return R.omit(
        [
            'authoredBy',
            'constructionTypes',
            'contractType',
            'createdAt',
            'projectUniqueId',
            'seasons',
            ...(limitedCostsFormFieldsToOmit || []),
            ...conversionIndicesKeysToOmit,
            ...seasonsInflationKeysToOmit,
            ...accumulationFieldsToOmit,
        ],
        {
            ...data,
            ...transformData,
            conversionIndices,
            seasonsInflation,
            plan,
            smrDistributions,
            otherDistributions,
        },
    );
};

interface IPriceParametersInput {
    supplyBasis: string;
    conversionIndices: Array<BimContractConversionIndexInput>;
    seasonsInflation: Array<BimContractSeasonInflationInput>;
    limitedCostCalcMethod: BimLimitedCostCalcMethodEnum;
    limitedCosts: Array<BimContractLimitedCostInput>;
}

export const initialPriceParametersToInput = (
    initialData: BimContractQuery['bimContract']['data'],
): IPriceParametersInput | null => {
    if (initialData) {
        const conversionIndices: Array<BimContractConversionIndexInput> =
            initialData?.conversionIndices?.map((ci) => ({
                constructionTypeId: ci.constructionType.id,
                coefficients: ci.coefficients,
            })) || [];

        const seasonsInflation: Array<BimContractSeasonInflationInput> =
            initialData?.seasonsInflation?.map((si) => ({
                contractSeasonYear: si.contractSeason.year,
                inflationRate: si.inflationRate,
            })) || [];

        const limitedCosts: Array<BimContractLimitedCostInput> =
            initialData?.limitedCosts?.map((ls) => ({
                constructionTypeId: ls.constructionType.id,
                regionalFactor: ls.regionalFactor,
                items: ls.items?.map((i) => ({ rateValue: i.rateValue, type: i.type })) || [],
            })) || [];

        return {
            supplyBasis: initialData.supplyBasis,
            limitedCostCalcMethod: initialData.limitedCostCalcMethod,
            conversionIndices,
            seasonsInflation,
            limitedCosts,
        };
    }

    return null;
};

const requestId = 'bimSaveContract';

interface IUseSaveContract {
    projectId?: string;
    contractId?: number;
}

export const useSaveContract = (props: IUseSaveContract) => {
    const { projectId, contractId } = props;
    const dispatch = useDispatch();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isSubmitted, setIsSubmitted] = useState(false);

    const [bimUpdateContract, bimUpdateContractStatus, bimUpdateContractError, bimUpdateContractRequest] = useRequest({
        requestId: `${requestId}_update`,
        query: bimUpdateContractGql,
        withEffect: false,
    });

    const analyticsData = useMemo(
        () => ({
            projectId: projectData?.uniqueId,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
            methodology: ProjectMethodologyEnum.Bim,
        }),
        [projectData?.contractType, projectData?.name, projectData?.subsidiary?.name, projectData?.uniqueId],
    );

    const sendContractCreatedEvent = useCallback(
        ({ payload }: TActionCommonInterface<BimCreateContractMutation>) => {
            const { bimCreateContract } = payload ?? {};

            const id = bimCreateContract?.data?.id;

            sendGpnAnalyticsEvent({
                componentId: 'rim_contract file_saved',
                constaeventtype: 'undefined',
                component: 'OTHER',
                contractId: id,
                ...analyticsData,
            });
        },
        [analyticsData, sendGpnAnalyticsEvent],
    );

    const [bimCreateContract, bimCreateContractStatus, bimCreateContractError, bimCreateContractRequest] = useRequest({
        requestId: `${requestId}_create`,
        query: bimCreateContractGql,
        withEffect: false,
        dependenciesUpdater: sendContractCreatedEvent,
    });

    useEffect(() => {
        if (bimCreateContractStatus === RequestStatus.SUCCESS && !bimCreateContract?.bimCreateContract?.result) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_contract file error uploading_notification',
                constaeventtype: 'change',
                component: 'ALERT',
                ...analyticsData,
            });
        }
    }, [analyticsData, bimCreateContract?.bimCreateContract?.result, bimCreateContractStatus, sendGpnAnalyticsEvent]);

    const [makeReset] = useResetStoreData({ requestId });
    const [makeResetUpdate] = useResetStoreData({ requestId: `${requestId}_update` });

    const data = useMemo(
        () => (contractId ? bimUpdateContract?.bimUpdateContract : bimCreateContract?.bimCreateContract),
        [bimCreateContract?.bimCreateContract, bimUpdateContract?.bimUpdateContract, contractId],
    );

    const status = useMemo(
        () => (contractId ? bimUpdateContractStatus : bimCreateContractStatus),
        [bimCreateContractStatus, bimUpdateContractStatus, contractId],
    );

    const error = useMemo(
        () => (contractId ? bimUpdateContractError : bimCreateContractError),
        [bimCreateContractError, bimUpdateContractError, contractId],
    );

    const reset = useCallback(() => {
        setIsSubmitted(false);
        makeReset(undefined);
        makeResetUpdate(undefined);
    }, [makeReset, makeResetUpdate]);

    const onCreateReportDownload = useCallback(() => {
        ExportCSVReport(
            contractId
                ? bimUpdateContract?.bimUpdateContract?.validation
                : bimCreateContract?.bimCreateContract?.validation,
        );

        if (!contractId) {
            sendGpnAnalyticsEvent({
                componentId: 'rim_contract file error upload_report created',
                ...analyticsData,
            });
        }
    }, [
        analyticsData,
        bimCreateContract?.bimCreateContract?.validation,
        bimUpdateContract?.bimUpdateContract?.validation,
        contractId,
        sendGpnAnalyticsEvent,
    ]);

    const makeRequest = useCallback(
        (formData: any, priceParameters: IPriceParametersInput | null) => {
            setIsSubmitted(true);

            // TODO any, [any, any] => any ...
            let contractInput = dataPrepare(formData) as any;

            if (priceParameters) {
                contractInput = { ...contractInput, ...priceParameters };
            }

            if (!contractId) {
                if (projectId) {
                    dispatch(
                        bimCreateContractRequest({
                            contractInput: {
                                ...contractInput,
                                projectUniqueId: projectId,
                            },
                        }),
                    );
                }
            } else {
                dispatch(
                    bimUpdateContractRequest({
                        contractInput: { ...contractInput, id: contractId },
                    }),
                );
            }
        },
        [bimCreateContractRequest, bimUpdateContractRequest, contractId, dispatch, projectId],
    );

    return { data, status, reset, error, makeRequest, isSubmitted, onCreateReportDownload };
};
